import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { AxiosResponse } from "axios";
import { serviceFetchSeasonalSales } from "src/api/service/services";

import default_image from "../../assets/default_sale.png";
import { getRetailerLogo } from "src/utils/retailers_list";


const SeasonalSale: React.FC = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [seasonalDealData, setSeasonalDealData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);


  useEffect(() => {
    serviceFetchSeasonalSales().then((response: AxiosResponse) => {

    const validDeals = (response?.data || [])
      .filter((deal: any) => 
        deal?.acf?.seasonal_sales_url &&
        deal?.acf?.seasonal_sales_url.trim() !== ""
      )
      .map((deal: any) => {
        // If the image is missing or empty, assign default image
        const image = deal?.acf?.seasonal_sales_image;
        if (!image || image.trim() === "") {
          deal.acf.seasonal_sales_image = default_image;
        }
        return deal;
      });

  //  console.log(validDeals);
    setSeasonalDealData(validDeals);
    })
     .finally(() => setLoading(false));
  }, []);

  const settings = {
    dots: false,
    infinite: true,
    speed: 1200,
    slidesToShow: isSmall ? 1 : isMedium ? 2 : 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    pauseOnHover: true,
  };

  const handleImagesLoad = () => {
    setImagesLoaded(true);
  };
  
  // 🧠 If loading is done and no data, don't render anything
  if (!loading && seasonalDealData.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        ml: "-50vw",
        mr: "-50vw",
        bgcolor: "#ffffff",
        py: { xs: 3, sm: 5 },
        boxShadow: `0px -1px 4px 0px #00000040, 0px 2px 6.7px 0px #0000001A inset, 0px 1px 10.2px 0px #00000040`,
      }}
    >
      <Box sx={{ mx: "auto", px: { xs: 2, sm: 4, md: 6 } }}>
        {/* Title Section */}
        <Box
          sx={{
            background: `linear-gradient(180deg, rgba(128, 28, 82, 0.96) -29.94%, rgba(170, 36, 72, 0.96) -4.39%, rgba(211, 38, 73, 0.96) 24.22%, rgba(248, 73, 68, 0.96) 52.83%, rgba(247, 129, 46, 0.96) 95.36%)`,
            color: "#fff",
            textAlign: "center",
            borderRadius: "12px",
            py: 1.5,
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              color: "#fff",
              fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2rem" },
            }}
          >
            Seasonal Sale
          </Typography>
        </Box>

        {/* Skeleton while loading */}
        {!imagesLoaded && seasonalDealData.length === 0 && (
          <Skeleton variant="rounded" height={200} animation="wave" />
        )}

         {/* Show slider only when valid data exists */}
        {seasonalDealData.length > 0 && (
        <Slider {...settings}>
          {seasonalDealData.map((deal: any, index) => {
            const retailer = deal?.acf?.seasonal_sales_retailer || "";
            const retailerLogo = getRetailerLogo(retailer);

            return (
              <Box key={index} px={{ xs: 1, sm: 1, md: 1 }}>
                <Card
                  sx={{
                    margin: "20px",
                    borderRadius: "16px",
                    overflow: "hidden",
                    aspectRatio: "1.4 / 1", // adjust for wide banners
                    boxShadow: "0px 0px 0.5px 0px #00000020",
                    position: "relative",
                    "&:hover": {
                      transform: "scale(1.02)",
                      transition: "all 0.3s ease-in-out",
                      boxShadow: "0px 6px 18px rgba(0,0,0,0.25)",
                    },
                  }}
                >
                  {/* Main Image */}
                  <CardMedia
                    component="img"
                    image={deal?.acf?.seasonal_sales_image}
                    alt={deal?.acf?.seasonal_sales_alt}
                    onClick={() =>
                      window.open(deal?.acf?.seasonal_sales_url, "_blank")
                    }
                    onLoad={handleImagesLoad}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      cursor: "pointer",
                    }}
                  />

                  {/* 🛍️ Retailer Logo Overlay */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 10,
                      right: 10,
                      backgroundColor: "rgba(255,255,255,0.9)",
                      borderRadius: "8px",
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                    }}
                  >
                    <img
                      src={retailerLogo}
                      alt={retailer}
                      style={{
                        width: 60,
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Card>
              </Box>
            );
          })}
        </Slider>
        )}
      </Box>
    </Box>
  );
};

export default SeasonalSale;
